import * as React from 'react';

/**
 * O Espelho da Nota Fiscal com cara de documento: cabeçalho da NF, quadro de
 * totais, produtos e os sub-relatórios (ST por item, parcelas, ST do
 * fornecedor), como o RTM do PBS. Os DADOS são os do SQL do dicionário
 * (RELATORIOS: SQL e SQL_SUB01..03, lidos ao vivo pela API); o DESENHO fica
 * aqui porque o RTM não mora no dicionário (é um arquivo do PBS).
 */

type Linha = Record<string, unknown>;

export interface ParteRelatorio {
  /** 0 = principal; 1, 2, 3 = sub-relatórios do PBS (ST, parcelas, ST do fornecedor). */
  sub: number;
  linhas: Linha[];
}

export const DATA_ISO = /^\d{4}-\d{2}-\d{2}T/;

/** Data do ERP (gravada no horário de Brasília): só o dia quando é meia-noite, senão dia e hora. */
export function dataPbs(v: string): string {
  const d = new Date(v);
  const hora = d.toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' });
  const dia = d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  return hora === '00:00' ? dia : `${dia} ${hora}`;
}

const num = (v: unknown, casas = 2): string =>
  typeof v === 'number' ? v.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas }) : v == null ? '' : String(v);
const qtd = (v: unknown): string => (typeof v === 'number' ? v.toLocaleString('pt-BR', { maximumFractionDigits: 3 }) : v == null ? '' : String(v));
const txt = (v: unknown): string => (v == null ? '' : typeof v === 'string' && DATA_ISO.test(v) ? dataPbs(v) : String(v).trim());

/** Uma coluna da grade: título, campo do SQL e se é número (alinha à direita). */
type Coluna = [titulo: string, valor: (l: Linha) => string, direita?: boolean, descricao?: boolean];

const PRODUTOS: Coluna[] = [
  ['Código', (l) => txt(l.PRODUTO)],
  ['Descrição', (l) => txt(l.DESCRICAO), false, true],
  ['Curva', (l) => txt(l.CURVA)],
  ['Qtd', (l) => qtd(l.QUANTIDADE), true],
  ['IPI %', (l) => num(l.IPI), true],
  ['ICMS %', (l) => num(l.ICMS), true],
  ['IVA', (l) => num(l.IVA), true],
  ['IVA aj.', (l) => num(l.IVA_AJUSTADO), true],
  ['ICMS venda', (l) => num(l.ICMS_VENDA), true],
  ['Pr. fábrica', (l) => num(l.FABRICA), true],
  ['Vlr. un. nota', (l) => num(l.PRECO_UNITARIO_NOTA), true],
  ['Desconto', (l) => `${num(l.DESCONTO)} ${txt(l.TP_DESC)}`, true],
  ['Custo', (l) => num(l.CUSTO_MEDIO), true],
  ['Mark-up', (l) => num(l.MARKUP), true],
  ['Rateio frete', (l) => num(l.RATEIO_FRETE), true],
  ['Pr. venda', (l) => num(l.PRECO_VENDA), true],
  ['Rent. %', (l) => num(l.RENTABILIDADE), true],
  ['Dif. %', (l) => num(l.DIFERENCA), true],
];

const ST: Coluna[] = [
  ['Produto', (l) => txt(l.NF_PRODUTO)],
  ['Descrição', (l) => txt(l.DESCRICAO), false, true],
  ['ST', (l) => txt(l.NF_SIT_TRIB)],
  ['Alíq.', (l) => num(l.NF_ALIQ_ICMS), true],
  ['IVA', (l) => num(l.CP_IVA), true],
  ['IVA aj.', (l) => num(l.CP_IVA_AJUS), true],
  ['Alíq. comp.', (l) => num(l.CP_ALIQ_ICMS), true],
  ['Alíq. subst.', (l) => num(l.CP_ALIQ_SUBST), true],
  ['Total prod.', (l) => num(l.TOTAL_PRODUTO), true],
  ['Total IVA', (l) => num(l.TOTAL_IVA), true],
  ['Total geral', (l) => num(l.TOTAL_GERAL), true],
  ['Créd. ICMS', (l) => num(l.CREDITO_ICMS), true],
  ['Subst. paga', (l) => num(l.SUBST_PAGA), true],
  ['Subst. a pagar', (l) => num(l.SUBST_PAGAR), true],
];

const PARCELAS: Coluna[] = [
  ['Vencimento', (l) => txt(l.VENCIMENTO)],
  ['%', (l) => num(l.PERCENTUAL), true],
  ['Valor', (l) => num(l.VALOR), true],
  ['Desc. financeiro', (l) => num(l.DESCONTO_FINANCEIRO), true],
  ['Líquido', (l) => num(l.LIQUIDO), true],
];

const ST_FORNECEDOR: Coluna[] = [
  ['Vencimento', (l) => txt(l.VENCIMENTO)],
  ['Título', (l) => txt(l.TITULO)],
  ['Valor', (l) => num(l.VALOR), true],
];

function Grade({ colunas, linhas }: { colunas: Coluna[]; linhas: Linha[] }): JSX.Element {
  return (
    <table className="esp-grade">
      <thead>
        <tr>
          {colunas.map(([t, , dir, desc]) => (
            <th key={t} className={[dir ? 'r' : '', desc ? 'esp-desc' : ''].join(' ')}>
              {t}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {linhas.map((l, i) => (
          <tr key={i}>
            {colunas.map(([t, valor, dir, desc]) => (
              <td key={t} className={[dir ? 'r' : '', desc ? 'esp-desc' : ''].join(' ')}>
                {valor(l)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function EspelhoFolha({ partes, nfCompra }: { partes: ParteRelatorio[]; nfCompra: number }): JSX.Element {
  const linhas = partes[0]?.linhas ?? [];
  const h = linhas[0] ?? {};
  const sub = (n: number): Linha[] => partes.find((p) => p.sub === n)?.linhas ?? [];
  const st = sub(1);
  const parcelas = sub(2);
  const stForn = sub(3);
  const s0 = st[0] ?? {};
  const info: [string, string][] = [
    ['Nota fiscal', `${txt(h.NF_NUMERO)} · série ${txt(h.NF_SERIE)}`],
    ['Espécie', txt(h.NF_ESPECIE)],
    ['Fornecedor', `${txt(h.ENTIDADE)} · ${txt(h.NOME)}`],
    ['CNPJ', txt(h.INSCRICAO_FEDERAL)],
    ['Inscrição estadual', txt(h.INSCRICAO_ESTADUAL)],
    ['Empresa / filial', `${txt(h.EMPRESA)} / ${txt(h.FILIAL)}`],
    ['Entrada (movimento)', txt(h.MOVIMENTO)],
    ['Pedido (OC)', txt(h.PEDIDO_COMPRA)],
    ['Recebimento (cega)', txt(h.RECEBIMENTO) || '—'],
    ['Digitada por', txt(h.USUARIO_NOME)],
  ];
  const totais: [string, unknown, boolean?][] = [
    ['Total de produtos', h.TOTAL_PRODUTOS],
    ['Total de IPI', h.TOTAL_IPI],
    ['Despesas', h.TOTAL_DESPESAS],
    ['Frete', h.TOTAL_FRETE],
    ['Seguro', h.TOTAL_SEGURO],
    ['ICMS substituto', h.TOTAL_SUBSTITUICAO],
    ['Repasse ICMS', h.TOTAL_REPASSE],
    ['Total de serviços', h.TOTAL_SERVICOS],
    ['TOTAL GERAL', h.TOTAL_GERAL, true],
    ['Base de cálculo ICMS', h.ICMS_BASE_CALCULO],
    ['Valor do ICMS', h.ICMS_VALOR],
  ];
  return (
    <div className="espelho">
      <header className="esp-topo">
        <div>
          <p className="esp-doc">Espelho da Nota Fiscal</p>
          <p className="esp-nf">NF {txt(h.NF_NUMERO)}</p>
          <p className="esp-sub">NF de compra (ERP) {nfCompra}</p>
        </div>
        <dl className="esp-info">
          {info.map(([k, v]) => (
            <React.Fragment key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </React.Fragment>
          ))}
        </dl>
        <table className="esp-totais">
          <tbody>
            {totais.map(([k, v, forte]) => (
              <tr key={k} className={forte ? 'forte' : undefined}>
                <td>{k}</td>
                <td>{num(v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </header>

      <h2 className="esp-secao">Produtos</h2>
      <Grade colunas={PRODUTOS} linhas={linhas} />

      {st.length ? (
        <>
          <h2 className="esp-secao">Detalhamento de substituição tributária</h2>
          <p className="esp-linha">
            Fornecedor {txt(s0.ENTIDADE)} · {txt(s0.NOME)} · UF {txt(s0.NF_ESTADO)} → {txt(s0.EMP_ESTADO)} · {txt(s0.EMPRESA_USUARIA)}
          </p>
          <Grade colunas={ST} linhas={st} />
        </>
      ) : null}

      {parcelas.length || stForn.length ? (
        <div className="esp-pe">
          {parcelas.length ? (
            <div>
              <h2 className="esp-secao">Parcelas financeiras</h2>
              <Grade colunas={PARCELAS} linhas={parcelas} />
            </div>
          ) : null}
          {stForn.length ? (
            <div>
              <h2 className="esp-secao">ST do fornecedor</h2>
              <Grade colunas={ST_FORNECEDOR} linhas={stForn} />
            </div>
          ) : null}
        </div>
      ) : null}

      <footer className="esp-rodape">
        <span>
          NF de compra {nfCompra} · NF {txt(h.NF_NUMERO)}
        </span>
        <span>Espelho da Nota Fiscal</span>
      </footer>
    </div>
  );
}

/** O papel do espelho: preto no branco, réguas finas, como o RTM. Vale na tela (escopo .espelho) e na impressão. */
export const CSS_ESPELHO = `
  .espelho { color: #000; background: #fff; font-family: Arial, 'Liberation Sans', sans-serif; font-size: 11px; min-width: 980px; }
  .espelho .esp-topo { display: grid; grid-template-columns: 150px 1fr 230px; gap: 16px; border: 1.5px solid #000; padding: 10px 12px; align-items: start; }
  .espelho .esp-doc { font-size: 10px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #333; margin: 0; }
  .espelho .esp-nf { font-size: 20px; font-weight: 800; margin: 2px 0 0; }
  .espelho .esp-sub { font-size: 10px; color: #444; margin: 2px 0 0; }
  .espelho .esp-info { display: grid; grid-template-columns: auto 1fr auto 1fr; gap: 3px 10px; margin: 0; }
  .espelho .esp-info dt { font-weight: 700; color: #333; white-space: nowrap; }
  .espelho .esp-info dd { margin: 0; }
  .espelho .esp-totais { width: 100%; border-collapse: collapse; border-left: 1px solid #000; }
  .espelho .esp-totais td { padding: 1.5px 6px; }
  .espelho .esp-totais td:first-child { font-weight: 700; color: #333; white-space: nowrap; }
  .espelho .esp-totais td:last-child { text-align: right; font-variant-numeric: tabular-nums; }
  .espelho .esp-totais tr.forte td { border-top: 1px solid #000; border-bottom: 1px solid #000; font-weight: 800; color: #000; font-size: 12px; }
  .espelho .esp-secao { margin: 12px 0 4px; padding: 3px 0; border-top: 1px solid #000; border-bottom: 1px solid #000; text-align: center; font-size: 10px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
  .espelho .esp-linha { margin: 0 0 4px; font-size: 10.5px; }
  .espelho .esp-grade { width: 100%; border-collapse: collapse; font-size: 10px; }
  .espelho .esp-grade th { background: #eee; border-top: 1px solid #777; border-bottom: 1px solid #777; padding: 3px 4px; font-size: 9px; text-transform: uppercase; letter-spacing: .03em; color: #333; text-align: left; white-space: nowrap; }
  .espelho .esp-grade td { padding: 2.5px 4px; border-bottom: 1px solid #e3e3e3; vertical-align: top; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .espelho .esp-grade tbody tr:last-child td { border-bottom: 1px solid #777; }
  .espelho .esp-grade .r { text-align: right; }
  .espelho .esp-grade .esp-desc { white-space: normal; min-width: 180px; max-width: 280px; }
  .espelho .esp-pe { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .espelho .esp-rodape { margin-top: 14px; padding-top: 4px; border-top: 1px dashed #999; display: flex; justify-content: space-between; font-size: 9px; color: #666; }
`;

/** Na impressão: A4 deitado, fonte em pontos. */
export const CSS_ESPELHO_PAPEL = `
  @page { size: A4 landscape; margin: 7mm; }
  ${CSS_ESPELHO}
  .espelho { min-width: 0; font-size: 8pt; }
  .espelho .esp-grade { font-size: 7pt; }
  .espelho .esp-grade th { font-size: 6pt; }
`;
